import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Check, KeyRound } from "lucide-react";
import { resetPasswordConfirm } from "@/services/authService";
import AuthLayout from "@/components/auth/AuthLayout";
import AuthField from "@/components/auth/AuthField";
import AuthAlert from "@/components/auth/AuthAlert";
import { SUPPORT_EMAIL, MIN_PASSWORD_LENGTH, PASSWORD_RULE } from "@/components/auth/authCopy";

export default function PasswordReset() {
    const { uid, token } = useParams<{ uid: string; token: string }>();

    const [newPassword, setNewPassword] = useState("");
    const [passwordError, setPasswordError] = useState<string | null>(null);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);

    const handlePasswordReset = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError("");

        if (newPassword.length < MIN_PASSWORD_LENGTH) {
            setPasswordError(`Your password needs at least ${MIN_PASSWORD_LENGTH} characters.`);
            return;
        }
        setPasswordError(null);

        if (!uid || !token) {
            setError("This reset link isn't valid. Ask for a new one and try again.");
            return;
        }

        try {
            setLoading(true);
            const res = await resetPasswordConfirm(uid, token, newPassword);
            if (res.status === 204) setSuccess(true);
            else setError("Failed to reset password. Please try again.");
        } catch (err) {
            // Surface the server's actual reason (e.g. the password-complexity
            // rules) instead of a generic failure the user cannot act on.
            setError(
                err instanceof Error && err.message
                    ? err.message
                    : "Failed to reset password. Please try again.",
            );
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <AuthLayout
                page="reset"
                variant="narrow"
                icon={<Check size={26} strokeWidth={2.5} />}
                title="Password changed"
                subtitle="You can sign in with your new password now."
            >
                <Link className="auth-btn-primary" to="/login">
                    Go to sign in
                </Link>

                <div className="auth-help auth-help--single d-only">
                    Something not right? Email{" "}
                    <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
                </div>
            </AuthLayout>
        );
    }

    return (
        <AuthLayout
            page="reset"
            variant="narrow"
            icon={<KeyRound size={26} strokeWidth={2} />}
            iconTone="brand"
            title="Set a new password"
            subtitle="Pick something you haven't used here before. You'll use it to sign in from now on."
        >
            <form onSubmit={handlePasswordReset} noValidate>
                <AuthField
                    fieldClassName="auth-field--password"
                    label="New password"
                    type="password"
                    name="new_password"
                    autoComplete="new-password"
                    placeholder="New password"
                    mobilePlaceholder="New password"
                    reveal
                    hint={PASSWORD_RULE}
                    value={newPassword}
                    error={passwordError}
                    onChange={(e) => {
                        setNewPassword(e.target.value);
                        setPasswordError(null);
                    }}
                />

                {error ? <AuthAlert>{error}</AuthAlert> : null}

                <button className="auth-btn-primary" type="submit" disabled={loading}>
                    {loading ? "Saving…" : "Save new password"}
                </button>
            </form>

            <Link className="auth-link auth-link--lg auth-back" to="/login">
                Back to sign in
            </Link>

            <div className="auth-help">
                <span className="q">Link expired or already used?</span>
                <span className="a">
                    <Link to="/forgot-password">Ask for a new link</Link> and we'll email you
                    another one.
                </span>
            </div>
        </AuthLayout>
    );
}
